/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { IStackProps, Stack, StackItem } from '@fluentui/react/lib/Stack';
import { getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { SavingState } from 'src/customText/appshell/dataTaggingPage/hooks/useSaveChanges';
import { customTextTeachingBubbleMessages } from 'src/customText/core/utils/customTextTeachingBubbleMessages';
import { createTeachingBubble, dequeueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';

export type ViewOptions = 'labeledFiles' | 'allFiles';

export type SamplingCommandBarProps = {
    onSave: () => void;
    onBack?: () => void;
    onNext?: () => void;
    savingState: SavingState;
    areNavButtonsDisplayed?: boolean;
    isBackDisabled?: boolean;
    isNextDisabled?: boolean;
};

const messages = defineMessages({
    back: {
        id: 'customText.samplingFooter.back',
        defaultMessage: 'Previous document'
    },
    next: {
        id: 'customText.samplingFooter.next',
        defaultMessage: 'Next document'
    },
    saveTags: {
        id: 'customText.samplingFooter.saveTags',
        defaultMessage: 'Save labels'
    }
});

const { styleComponent } = getStylistV2('SamplingFooter');
const Footer = styleComponent<IStackProps>(Stack)('Footer', theme => ({
    minHeight: 64,
    height: 'auto',
    overflowX: 'auto',
    borderTop: `1px solid ${theme.footerBorderColor}`
}));

/**
 * Footer used for navigating through the documents,
 * and/or save changes
 */
export const SamplingFooter = observer((props: SamplingCommandBarProps) => {
    const { areNavButtonsDisplayed = true, isBackDisabled, isNextDisabled, onBack, onNext, onSave, savingState } = props;
    const { localize } = useLocalization();
    const { rootStore, uiStore } = useGlobalState();
    const { isMediumOrGreater } = useResponsiveMode();

    const saveTagsButtonIdRef = React.useRef<string>(getId());
    const nextButtonIdRef = React.useRef<string>(getId());

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    React.useEffect(
        () => () => {
            dequeueTeachingBubble('CustomTextSaveTagsButton');
            dequeueTeachingBubble('CustomTextMovingBetweenDocuments');
        },
        []
    );

    React.useEffect(() => {
        if (
            canWrite &&
            savingState === 'dirty' &&
            uiStore.teachingBubbles.CustomTextTrainingLimitsUI &&
            !uiStore.teachingBubbles.CustomTextSaveTagsButton &&
            !uiStore.skippedTeachingBubbles.CustomTextTrainingLimitsUI
        ) {
            createTeachingBubble(
                'CustomTextSaveTagsButton',
                localize(customTextTeachingBubbleMessages.saveTagsButtonBody),
                localize(customTextTeachingBubbleMessages.saveTagsButtonTitle),
                `#${saveTagsButtonIdRef.current}`,
                true,
                true
            );

            return;
        }

        if (
            !isNextDisabled &&
            !uiStore.teachingBubbles.CustomTextMovingBetweenDocuments &&
            (uiStore.teachingBubbles.CustomTextSaveTagsButton || uiStore.teachingBubbles.CustomTextTrainingLimitsUI) &&
            (!uiStore.skippedTeachingBubbles.CustomTextSaveTagsButton || !uiStore.skippedTeachingBubbles.CustomTextTrainingLimitsUI)
        ) {
            createTeachingBubble(
                'CustomTextMovingBetweenDocuments',
                localize(customTextTeachingBubbleMessages.moveBetweenDocumentsBody),
                localize(customTextTeachingBubbleMessages.moveBetweenDocumentsTitle),
                `#${nextButtonIdRef.current}`,
                true
            );
        }
    }, [canWrite, isNextDisabled, savingState, Object.values(uiStore.teachingBubbles)]);

    return (
        <Footer
            verticalAlign="center"
            tokens={{ childrenGap: 8, padding: 15 }}
            horizontal={isMediumOrGreater}
            horizontalAlign={areNavButtonsDisplayed ? 'space-between' : 'end'}>
            {areNavButtonsDisplayed && (
                <StackItem>
                    <Stack horizontal={isMediumOrGreater} reversed tokens={{ childrenGap: 8 }}>
                        <PrimaryButton id={nextButtonIdRef.current} onClick={onNext} disabled={isNextDisabled}>
                            {localize(messages.next)}
                        </PrimaryButton>

                        <DefaultButton onClick={onBack} disabled={isBackDisabled}>
                            {localize(messages.back)}
                        </DefaultButton>
                    </Stack>
                </StackItem>
            )}

            <StackItem>
                <RBACTooltip
                    displayedUserRole={displayedUserRole}
                    hidden={canWrite || savingState !== 'dirty'}
                    data-automation-id="saveTagsButtonLanguageToolTip"
                    componentSurroundedByToolTip={
                        <DefaultButton
                            onClick={onSave}
                            id={saveTagsButtonIdRef.current}
                            iconProps={{ iconName: 'Save' }}
                            disabled={!canWrite || savingState !== 'dirty'}>
                            {localize(messages.saveTags)}
                        </DefaultButton>
                    }
                />
            </StackItem>
        </Footer>
    );
});
